import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@medical/shared/contexts/AuthContext';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';
import toast from 'react-hot-toast';
import { extractMedicationPayload } from '@medical/shared/utils/medicationUtils';
import { useTranslation } from 'react-i18next';
import { TranslatedText } from '@medical/shared/components/TranslatedText';

const MessagesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;

  @media (max-width: 768px) {
    padding: 1rem 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0.5rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const PageCloseButton = styled.button`
  background: #f3f4f6;
  border: none;
  border-radius: 0.5rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  color: #6b7280;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    font-size: 1.25rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin: 0 0 0.25rem 0;
  }
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 1rem;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }

  @media (max-width: 480px) {
    font-size: 0.875rem;
  }
`;

const MessagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MessageCard = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => {
    if (props.type === 'medication') return '#059669';
    if (props.type === 'appointment') return '#3b82f6';
    if (props.type === 'advice') return '#f59e0b';
    if (props.type === 'instruction') return '#8b5cf6';
    return '#6b7280';
  }};
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: ${props => props.unread ? 1 : 0.8};

  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    border-radius: 0.5rem;
    border-left-width: 3px;
  }
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
`;

const MessageInfo = styled.div`
  flex: 1;
`;

const MessageType = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  background: ${props => {
    if (props.type === 'medication') return '#d1fae5';
    if (props.type === 'appointment') return '#dbeafe';
    if (props.type === 'advice') return '#fef3c7';
    if (props.type === 'instruction') return '#ede9fe';
    return '#f3f4f6';
  }};
  color: ${props => {
    if (props.type === 'medication') return '#065f46';
    if (props.type === 'appointment') return '#1e40af';
    if (props.type === 'advice') return '#92400e';
    if (props.type === 'instruction') return '#5b21b6';
    return '#374151';
  }};
`;

const MessageSubject = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.5rem 0;
`;

const MessageFrom = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const MessageTime = styled.div`
  font-size: 0.875rem;
  color: #9ca3af;
  text-align: right;
`;

const MessageBody = styled.div`
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
  margin-bottom: 1rem;
`;

const UnreadBadge = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  margin-left: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 1rem;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 2rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
`;

const EmptyText = styled.p`
  margin: 0;
  color: #6b7280;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 1rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  position: relative;

  @media (max-width: 768px) {
    max-height: 95vh;
    border-radius: 0.75rem;
  }

  @media (max-width: 480px) {
    border-radius: 0.5rem;
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  margin-left: 1rem;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    width: 28px;
    height: 28px;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const ModalMessageType = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 1rem;
  background: ${props => {
    if (props.type === 'medication') return '#d1fae5';
    if (props.type === 'appointment') return '#dbeafe';
    if (props.type === 'advice') return '#fef3c7';
    if (props.type === 'instruction') return '#ede9fe';
    return '#f3f4f6';
  }};
  color: ${props => {
    if (props.type === 'medication') return '#065f46';
    if (props.type === 'appointment') return '#1e40af';
    if (props.type === 'advice') return '#92400e';
    if (props.type === 'instruction') return '#5b21b6';
    return '#374151';
  }};
`;

const ModalMessageText = styled.div`
  color: #374151;
  line-height: 1.8;
  white-space: pre-wrap;
  font-size: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
`;

const ModalMessageInfo = styled.div`
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
  font-size: 0.875rem;
  color: #6b7280;
`;

const ModalActionButton = styled.button`
  background: ${props => props.primary ? '#3b82f6' : '#f3f4f6'};
  color: ${props => props.primary ? 'white' : '#374151'};
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1rem;
  width: 100%;

  &:hover {
    background: ${props => props.primary ? '#2563eb' : '#e5e7eb'};
  }

  @media (max-width: 480px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.9375rem;
  }
`;

const Messages = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    fetchMessages();
    // Refresh notification count when Messages page loads
    window.dispatchEvent(new Event('refreshUnreadCount'));
  }, []);

  const handleCloseModal = () => {
    setSelectedMessage(null);
  };

  useEffect(() => {
    // Handle Escape key to close modal
    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectedMessage) {
        handleCloseModal();
      }
    };

    if (selectedMessage) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [selectedMessage, handleCloseModal]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/messages', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setMessages(response.data.messages || []);
        // Refresh notification count after fetching messages
        window.dispatchEvent(new Event('refreshUnreadCount'));
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      // Update local state immediately for better UX
      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg._id === messageId
            ? { ...msg, isRead: true, readAt: new Date() }
            : msg
        )
      );
      
      // Mark as read on server
      await axios.put(`/api/messages/${messageId}/read`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Refresh bell icon count after marking as read
      window.dispatchEvent(new Event('refreshUnreadCount'));
    } catch (error) {
      console.error('Error marking message as read:', error);
      // Refresh count even on error to ensure consistency
      window.dispatchEvent(new Event('refreshUnreadCount'));
    }
  };

  const handleMessageClick = (message) => {
    // Mark as read if unread
    if (!message.isRead) {
      markAsRead(message._id);
    }
    
    // If it's a medication message, navigate to medication page
    if (message.type === 'medication') {
      navigate('/medication');
      return;
    }
    
    // For other message types, show modal
    setSelectedMessage(message);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseModal();
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      medication: t('messages.types.medication'),
      appointment: t('messages.types.appointment'),
      advice: t('messages.types.advice'),
      instruction: t('messages.types.instruction'),
      general: t('messages.types.general')
    };
    return labels[type] || t('messages.types.general');
  };

  const formatMedicationNotification = (message) => {
    // Extract medication payload from message
    const { payload } = extractMedicationPayload(message.message);
    
    if (payload && payload.medications && payload.medications.length > 0) {
      // Get tablet names
      const tabletNames = payload.medications
        .map(med => med.name)
        .filter(Boolean);
      
      if (tabletNames.length > 0) {
        if (tabletNames.length === 1) {
          return t('messages.medicationNotification.single', { medication: tabletNames[0] });
        } else {
          // Create a copy to avoid mutating the original array
          const namesCopy = [...tabletNames];
          const lastMed = namesCopy.pop();
          const medsList = namesCopy.join(', ');
          return t('messages.medicationNotification.multiple', { meds: medsList, lastMed });
        }
      }
    }
    
    // Fallback for legacy or non-structured messages
    return t('messages.medicationNotification.fallback');
  };

  if (loading) {
    return <LoadingSpinner text={t('messages.loading')} />;
  }

  const unreadCount = messages.filter(msg => !msg.isRead).length;

  return (
    <>
      <MessagesContainer>
        <Header>
          <HeaderContent>
            <Title>{t('messages.title')}</Title>
            <Subtitle>
              {unreadCount > 0 ? (
                unreadCount === 1 
                  ? t('messages.subtitle.unread', { count: unreadCount })
                  : t('messages.subtitle.unreadPlural', { count: unreadCount })
              ) : (
                <>{t('messages.subtitle.none')}</>
              )}
            </Subtitle>
          </HeaderContent>
          <PageCloseButton onClick={() => navigate('/body-diagram')} aria-label="Close notifications">
            ×
          </PageCloseButton>
        </Header>

        {messages.length > 0 ? (
          <MessagesList>
            {messages.map((message) => (
              <MessageCard
                key={message._id}
                type={message.type}
                unread={!message.isRead}
                onClick={() => handleMessageClick(message)}
              >
                <MessageHeader>
                  <MessageInfo>
                    <MessageType type={message.type}>
                      {getTypeLabel(message.type)}
                    </MessageType>
                    {message.subject && (
                      <MessageSubject>
                        <TranslatedText text={message.subject} />
                        {!message.isRead && <UnreadBadge />}
                      </MessageSubject>
                    )}
                    <MessageFrom>
                      {t('messages.from')} Dr. {message.doctorId?.firstName || ''} {message.doctorId?.lastName || ''}
                    </MessageFrom>
                  </MessageInfo>
                  <MessageTime>
                    {format(new Date(message.createdAt), 'MMM dd, yyyy')}
                    <br />
                    {format(new Date(message.createdAt), 'h:mm a')}
                  </MessageTime>
                </MessageHeader>
                <MessageBody>
                  {message.type === 'medication' 
                    ? formatMedicationNotification(message)
                    : <TranslatedText text={message.message} />}
                </MessageBody>
              </MessageCard>
            ))}
          </MessagesList>
        ) : (
          <EmptyState>
            <EmptyIcon>📬</EmptyIcon>
            <EmptyTitle>{t('messages.empty.title')}</EmptyTitle>
            <EmptyText>{t('messages.empty.text')}</EmptyText>
          </EmptyState>
        )}
      </MessagesContainer>

      {selectedMessage && (
        <ModalOverlay onClick={handleOverlayClick}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {selectedMessage.subject ? (
                  <TranslatedText text={selectedMessage.subject} />
                ) : (
                  getTypeLabel(selectedMessage.type)
                )}
              </ModalTitle>
              <CloseButton onClick={handleCloseModal} aria-label="Close">
                ×
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <ModalMessageType type={selectedMessage.type}>
                {getTypeLabel(selectedMessage.type)}
              </ModalMessageType>
              <ModalMessageText>
                {selectedMessage.type === 'medication' 
                  ? formatMedicationNotification(selectedMessage)
                  : <TranslatedText text={selectedMessage.message} />}
              </ModalMessageText>
              <ModalMessageInfo>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>{t('messages.from')}</strong> Dr. {selectedMessage.doctorId?.firstName || ''} {selectedMessage.doctorId?.lastName || ''}
                </div>
                <div>
                  <strong>{t('messages.date') || 'Date'}:</strong> {format(new Date(selectedMessage.createdAt), 'MMM dd, yyyy')} at {format(new Date(selectedMessage.createdAt), 'h:mm a')}
                </div>
              </ModalMessageInfo>
              {selectedMessage.type === 'medication' && (
                <ModalActionButton primary onClick={() => {
                  handleCloseModal();
                  navigate('/medication');
                }}>
                  {t('messages.viewMedication') || 'View Medication'}
                </ModalActionButton>
              )}
              <ModalActionButton onClick={handleCloseModal}>
                {t('messages.close') || 'Close'}
              </ModalActionButton>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default Messages;
